/** Wiki Health: findings computed from the project's pages, Sources, and search analytics (no LLM) */

import type { ProjectSummary } from './project'

export type HealthFindingType = 'superseded_source' | 'contradiction' | 'stale_page' | 'search_gap'

/** Days without a new cited Source before a page counts as stale, unless the request says otherwise */
export const HEALTH_DEFAULT_STALE_DAYS = 30
/** Window of search analytics read for gaps */
export const HEALTH_GAP_DAYS = 30

export interface HealthPageRef {
  id: string
  slug: string
  title: string
}

export interface HealthSourceRef {
  id: string
  title: string
  filename: string
}

interface HealthFindingBase {
  /** Stable id of the finding; used to dismiss it */
  key: string
  type: HealthFindingType
  /** Shown when the request asked for dismissed findings too */
  dismissed: boolean
}

/** A live page cites a Source that a newer version replaced */
export interface SupersededSourceFinding extends HealthFindingBase {
  type: 'superseded_source'
  page: HealthPageRef
  source: HealthSourceRef
  /** Direct replacement (a proposed fix cites the newest active version in its chain) */
  replacement: HealthSourceRef | null
  /** Sentences on the page citing the old Source */
  sentences: number
  /**
   * The citation swap needs no judgment: the replacement has the same text as the old Source (line endings,
   * trailing whitespace, and trailing blank lines aside). Certain fixes are proposed automatically; any other change
   * waits for someone to propose it and goes through review.
   */
  certain: boolean
}

/** Two sentences on different pages that look like the same statement with other numbers or a flipped negation */
export interface ContradictionFinding extends HealthFindingBase {
  type: 'contradiction'
  reason: 'number' | 'negation'
  a: { page: HealthPageRef; sentence: string }
  b: { page: HealthPageRef; sentence: string }
}

/** The newest Source a page cites is older than the stale threshold */
export interface StalePageFinding extends HealthFindingBase {
  type: 'stale_page'
  page: HealthPageRef
  /** `sources_changed`: a cited Source has a newer version; `no_new_sources`: nothing new was cited */
  reason: 'sources_changed' | 'no_new_sources'
  newestSourceAt: number
  pageUpdatedAt: number
  /** Days since the newest cited Source was added */
  days: number
}

/** A query in the project's scope that found nothing */
export interface SearchGapFinding extends HealthFindingBase {
  type: 'search_gap'
  query: string
  count: number
  lastSearchedAt: number
  /** Where the query came from: search, context, ask */
  kinds: string[]
}

export type HealthFinding = SupersededSourceFinding | ContradictionFinding | StalePageFinding | SearchGapFinding

export interface HealthReport {
  project: Pick<ProjectSummary, 'id' | 'slug' | 'name'>
  generatedAt: number
  staleDays: number
  /** Open findings per type (dismissed ones excluded) */
  counts: Record<HealthFindingType, number>
  dismissed: number
  /** A per-type cap was hit; the counts cover what was examined */
  truncated: boolean
  /** Total findings matching the filter, before limit/offset */
  total: number
  /**
   * search_gap findings show what other members searched for and asked, so like Search analytics they are listed
   * only for admins (API keys: the tenant:manage scope); false when they were left out
   */
  gapsIncluded: boolean
  findings: HealthFinding[]
}

/** Outcome of applying the certain fixes of a project */
export interface HealthAutoFixResult {
  /** proposals written, with their review status */
  proposals: Array<{ key: string; slug: string; proposalId: string; status: 'applied' | 'pending' }>
  /** certain fixes that could not be written (the page moved, a check blocked it, ...) */
  failed: Array<{ key: string; slug: string; error: string }>
}
